import config from "../../config.json";
const BASE_AUTH_API = config.BASE_AUTH_API;

const authHeaders = (token) => ({
  Accept: "application/json",
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

// Read helpers resolve to undefined on failure (callers treat that as an
// error state); mutation helpers further down throw an error that carries the
// HTTP status so the UI can explain what happened.
const getJson = async (url, token, pick) => {
  try {
    const response = await fetch(url, { method: "GET", headers: authHeaders(token) });
    const data = await response.json();
    return data.success ? pick(data) : undefined;
  } catch (error) {
    return undefined;
  }
};

export const fetchActionsAPI = (token, data) => {
  const params = new URLSearchParams({
    storeId: data.storeId,
    appName: data.appName,
    status: data?.status || "",
    type: data?.type || "",
  });
  return getJson(`${BASE_AUTH_API}actions?${params}`, token, (body) => body.actions);
};

export const fetchActionsSummaryAPI = (token, data) => {
  const params = new URLSearchParams({ storeId: data.storeId, appName: data.appName });
  return getJson(`${BASE_AUTH_API}actions/status-summary?${params}`, token, (body) => body.summary);
};

export const fetchActionsfullSummaryAPI = (token, sId) => {
  const params = new URLSearchParams();
  sId.forEach((id) => params.append("storeId", id));
  return getJson(`${BASE_AUTH_API}actions/status-full-summary?${params}`, token, (body) => body.summary);
};

const sendJson = async (path, token, method, body) => {
  try {
    const response = await fetch(`${BASE_AUTH_API}${path}`, {
      method,
      headers: authHeaders(token),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const data = await response.json();
    return data.success ? data : undefined;
  } catch (error) {
    return undefined;
  }
};

export const deleteActionAPI = async (token, id) => {
  const data = await sendJson(`actions/${id}`, token, "DELETE");
  return data ? data.action || data.deletedAction || data : undefined;
};

// ---- Campaign actions: these throw (error.status / error.message) --------

async function campaignRequest(path, token, method, body) {
  let response;
  try {
    response = await fetch(`${BASE_AUTH_API}${path}`, {
      method,
      headers: authHeaders(token),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    const error = new Error("Network error");
    error.status = 0;
    throw error;
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    const error = new Error(data.message || data.error || "Request failed");
    error.status = response.status;
    throw error;
  }
  return data;
}

// Creates one campaign per store in `storeIds` (see POST actions/campaigns).
export const createCampaignAPI = async (token, payload) =>
  (await campaignRequest("actions/campaigns", token, "POST", payload)).actions;

export const editCampaignAPI = async (token, id, changes) =>
  (await campaignRequest(`actions/${id}/campaign`, token, "PATCH", changes)).action;

// Delegated approval (am / dmm only): needs a reason and an explicit confirmation.
export const approveOnBehalfAPI = async (token, id, reason) =>
  (await campaignRequest(`actions/${id}/approve-on-behalf`, token, "POST", { reason, confirm: true })).action;

// The approval of a campaign request: the restaurant first, then LUD. A rejection
// always carries a reason (the server refuses an empty one).
export const decideCampaignAPI = async (token, id, approved, reason) =>
  (await campaignRequest(`actions/${id}/approve`, token, "PATCH", { approved, ...(reason ? { reason } : {}) })).action;

// Activation / completion (LUD). Leaving "pending" is only possible through the approvals.
export const changeCampaignStatusAPI = async (token, id, status) =>
  (await campaignRequest(`actions/${id}/status`, token, "PATCH", { status })).action;

export const cancelCampaignAPI = async (token, id, reason) =>
  (await campaignRequest(`actions/${id}/cancel`, token, "POST", { reason })).action;

export const deleteCampaignRequestAPI = async (token, id) =>
  (await campaignRequest(`actions/${id}/delete-request`, token, "POST")).action;

// Operations requests made from catalog campaigns, one record per restaurant
// (limited by the server to the restaurants the user can access).
export const fetchCatalogRequestsAPI = (token) =>
  getJson(`${BASE_AUTH_API}actions/catalog-requests`, token, (body) => body.actions);
